import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Copy, KeyRound, Loader2, Pencil, Plus, UserX, UserCheck } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { roleLabel } from '@/components/shell/app-sidebar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/lib/api'
import type { StaffUser } from '@/lib/session'
import { reportError } from '@/lib/settings'
import { FormField, SettingsCard, TextInput } from './form-kit'
import { PageTitle } from './pages'

interface Account {
  id: string
  email: string
  name: string
  roles: string[]
  active: boolean
  mustChangePassword: boolean
  lastLoginAt: string | null
}

const ROLE_HELP: [string, string][] = [
  ['owner', 'Approves prices and commitments; sees everything.'],
  ['platform_admin', 'Manages settings, users and integrations.'],
  ['commercial_manager', 'Builds and sends quotes; manages the price book; can approve.'],
  ['sales_admin', 'Enquiries, surveys and draft quotes.'],
  ['hr_admin', 'New starters and onboarding. Sees People data.'],
  ['ops_manager', 'Mobilisation and operational handover.'],
  ['auditor', 'Read-only access to records and the audit trail.'],
]

const when = (iso: string | null) =>
  iso
    ? new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(iso))
    : 'Never'

export function UsersPage({ me }: { me: StaffUser }) {
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ['staff'],
    queryFn: async () => (await api.get<{ items: Account[] }>('/staff')).items,
  })
  const refresh = () => void qc.invalidateQueries({ queryKey: ['staff'] })
  const [editing, setEditing] = useState<{
    account: Account | null
    name: string
    email: string
    roles: string[]
  } | null>(null)
  const [secret, setSecret] = useState<{ name: string; email: string; password: string } | null>(
    null,
  )
  const [confirmReset, setConfirmReset] = useState<Account | null>(null)

  const create = useMutation({
    mutationFn: (b: { name: string; email: string; roles: string[] }) =>
      api.post<{ user: Account; temporaryPassword: string }>('/staff', b),
    onSuccess: ({ user, temporaryPassword }) => {
      refresh()
      setEditing(null)
      setSecret({ name: user.name, email: user.email, password: temporaryPassword })
    },
    onError: (e) => reportError(e),
  })
  const patch = useMutation({
    mutationFn: ({ id, ...b }: { id: string; name?: string; roles?: string[]; active?: boolean }) =>
      api.patch<Account>(`/staff/${id}`, b),
    onSuccess: (a, vars) => {
      refresh()
      setEditing(null)
      toast.success(
        vars.active === false
          ? `${a.name} can no longer sign in`
          : vars.active === true
            ? `${a.name} can sign in again`
            : 'Account saved',
      )
    },
    onError: (e) => reportError(e),
  })
  const reset = useMutation({
    mutationFn: (a: Account) =>
      api.post<{ temporaryPassword: string }>(`/staff/${a.id}/reset-password`),
    onSuccess: ({ temporaryPassword }, a) => {
      setConfirmReset(null)
      refresh()
      setSecret({ name: a.name, email: a.email, password: temporaryPassword })
    },
    onError: (e) => reportError(e),
  })

  const errors: Record<string, string> = {}
  if (editing) {
    if (!editing.name.trim()) errors.name = 'Enter their name.'
    if (!editing.account && !/^\S+@\S+\.\S+$/.test(editing.email))
      errors.email = 'Enter a valid email address.'
    if (editing.roles.length === 0) errors.roles = 'Choose at least one role.'
  }
  const valid = Object.keys(errors).length === 0

  return (
    <>
      <PageTitle
        title="Users and roles"
        description="Everyone who can sign in to HCD Portal. New people get a temporary password they must change the first time they sign in."
      />
      <SettingsCard
        title="People"
        action={
          <Button
            className="h-10"
            onClick={() =>
              setEditing({ account: null, name: '', email: '', roles: ['sales_admin'] })
            }
          >
            <Plus />
            Add person
          </Button>
        }
      >
        {list.isPending ? (
          <Skeleton className="h-32" />
        ) : list.isError ? (
          <p role="alert" className="text-destructive">
            {list.error.message}
          </p>
        ) : (
          <ul className="-my-2 divide-y">
            {list.data.map((a) => (
              <li key={a.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 font-medium">
                    {a.name}
                    {a.id === me.id && <Badge variant="secondary">You</Badge>}
                    {!a.active && <Badge variant="outline">Cannot sign in</Badge>}
                    {a.active && a.mustChangePassword && (
                      <Badge variant="outline">Temporary password</Badge>
                    )}
                  </div>
                  <div className="text-sm break-all text-muted-foreground">{a.email}</div>
                  <div className="mt-1 text-[13px] text-muted-foreground">
                    {a.roles.map(roleLabel).join(', ')} · last signed in {when(a.lastLoginAt)}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon-lg"
                    aria-label={`Edit ${a.name}`}
                    onClick={() =>
                      setEditing({ account: a, name: a.name, email: a.email, roles: a.roles })
                    }
                  >
                    <Pencil />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon-lg"
                    aria-label={`Issue a new temporary password for ${a.name}`}
                    onClick={() => setConfirmReset(a)}
                    disabled={!a.active}
                  >
                    <KeyRound />
                  </Button>
                  {a.id !== me.id && (
                    <Button
                      variant="ghost"
                      size="icon-lg"
                      aria-label={
                        a.active ? `Stop ${a.name} signing in` : `Let ${a.name} sign in again`
                      }
                      onClick={() => patch.mutate({ id: a.id, active: !a.active })}
                      disabled={patch.isPending}
                    >
                      {a.active ? <UserX /> : <UserCheck />}
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SettingsCard>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing?.account ? `Edit ${editing.account.name}` : 'Add a person'}
            </DialogTitle>
            <DialogDescription>
              {editing?.account
                ? 'Role changes apply straight away.'
                : 'You will see a temporary password to give them. Nothing is emailed.'}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault()
                if (!valid) return
                if (editing.account)
                  patch.mutate({ id: editing.account.id, name: editing.name, roles: editing.roles })
                else
                  create.mutate({
                    name: editing.name,
                    email: editing.email.trim(),
                    roles: editing.roles,
                  })
              }}
            >
              <FormField label="Name" error={errors.name}>
                {(p) => (
                  <TextInput
                    {...p}
                    autoFocus
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                )}
              </FormField>
              <FormField
                label="Email"
                hint={editing.account ? 'Email addresses cannot be changed.' : undefined}
                error={errors.email}
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="email"
                    inputMode="email"
                    autoCapitalize="none"
                    value={editing.email}
                    disabled={!!editing.account}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  />
                )}
              </FormField>
              <fieldset className="grid gap-2">
                <legend className="mb-1 text-sm font-medium">Roles</legend>
                {ROLE_HELP.map(([role, help]) => (
                  <label
                    key={role}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border p-3 has-checked:border-primary has-checked:bg-accent/40"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 size-4 accent-(--primary)"
                      checked={editing.roles.includes(role)}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          roles: e.target.checked
                            ? [...editing.roles, role]
                            : editing.roles.filter((r) => r !== role),
                        })
                      }
                    />
                    <span>
                      <span className="block text-sm font-medium">{roleLabel(role)}</span>
                      <span className="block text-[13px] text-muted-foreground">{help}</span>
                    </span>
                  </label>
                ))}
                {errors.roles && (
                  <p className="text-[13px] font-medium text-destructive">{errors.roles}</p>
                )}
              </fieldset>
              <DialogFooter className="gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10"
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="h-10"
                  disabled={!valid || create.isPending || patch.isPending}
                >
                  {(create.isPending || patch.isPending) && <Loader2 className="animate-spin" />}
                  {editing.account ? 'Save changes' : 'Add person'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmReset} onOpenChange={(o) => !o && setConfirmReset(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Issue a new temporary password?</DialogTitle>
            <DialogDescription>
              {confirmReset?.name} will be signed out everywhere and must choose a new password next
              time they sign in.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="h-10" onClick={() => setConfirmReset(null)}>
              Cancel
            </Button>
            <Button
              className="h-10"
              onClick={() => confirmReset && reset.mutate(confirmReset)}
              disabled={reset.isPending}
            >
              {reset.isPending && <Loader2 className="animate-spin" />}
              Issue password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Temporary password for {secret?.name}</DialogTitle>
            <DialogDescription>
              Give this to them in person or by phone. It is shown only once and must be changed
              when they first sign in.
            </DialogDescription>
          </DialogHeader>
          {secret && <SecretBox email={secret.email} password={secret.password} />}
          <DialogFooter>
            <Button className="h-10" onClick={() => setSecret(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function SecretBox({ email, password }: { email: string; password: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="grid gap-2 rounded-lg border bg-muted p-4">
      <div className="text-sm text-muted-foreground">Email</div>
      <div className="font-medium break-all">{email}</div>
      <div className="mt-2 text-sm text-muted-foreground">Temporary password</div>
      <div className="flex items-center justify-between gap-2">
        <code
          data-testid="temporary-password"
          className="tabular text-lg font-semibold tracking-wide"
        >
          {password}
        </code>
        <Button
          variant="outline"
          className="h-9"
          onClick={() => {
            void navigator.clipboard?.writeText(password).then(() => setCopied(true))
          }}
        >
          {copied ? <Check /> : <Copy />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  )
}
